import React from 'react';
import {
  Box,
  Heading,
  SimpleGrid,
  Card,
  CardBody,
  CardHeader,
  Text,
  Button,
  VStack,
  HStack,
  Icon,
  Alert,
  AlertIcon,
  Divider,
} from '@chakra-ui/react';
import { FiClock, FiLock, FiMapPin, FiShoppingCart, FiCalendar, FiDollarSign, FiGift, FiTag, FiMail, FiFileText, FiHelpCircle, FiVolume2, FiSearch } from 'react-icons/fi';
import { Link } from 'react-router-dom';

interface SettingsOption {
  id: string;
  title: string;
  description: string;
  icon: any;
  path: string;
  color: string;
}

const settingsOptions: SettingsOption[] = [
  {
    id: 'business-hours',
    title: 'Business Hours',
    description: 'Configure your operating hours for each day of the week',
    icon: FiClock,
    path: '/settings/business-hours',
    color: 'blue',
  },
  {
    id: 'booking-constraints',
    title: 'Booking Constraints',
    description: 'Set rules for advance booking, same-day bookings, and scheduling limits',
    icon: FiLock,
    path: '/settings/booking-constraints',
    color: 'purple',
  },
  {
    id: 'service-area',
    title: 'Service Area',
    description: 'Define geographic coverage zones and delivery radius',
    icon: FiMapPin,
    path: '/settings/service-area',
    color: 'green',
  },
  {
    id: 'booking-pools',
    title: 'Booking Pools',
    description: 'Configure pool timing, discounts, and membership settings',
    icon: FiShoppingCart,
    path: '/settings/booking-pools',
    color: 'orange',
  },
  {
    id: 'payment-settings',
    title: 'Payment Settings',
    description: 'Configure payment strategies, payouts, and refund policies',
    icon: FiDollarSign,
    path: '/settings/payment',
    color: 'teal',
  },
  {
    id: 'holidays',
    title: 'Holiday Calendar',
    description: 'Manage holidays and special closure dates',
    icon: FiCalendar,
    path: '/settings/holidays',
    color: 'red',
  },
  {
    id: 'gift-card-templates',
    title: 'Gift Card Templates',
    description: 'Configure gift card designs, denominations, and seasonal templates',
    icon: FiGift,
    path: '/settings/gift-card-templates',
    color: 'pink',
  },
  {
    id: 'promo-codes',
    title: 'Promo Codes',
    description: 'Create and manage promotional discount codes for customers',
    icon: FiTag,
    path: '/settings/promo-codes',
    color: 'cyan',
  },
  {
    id: 'email-settings',
    title: 'Email Settings',
    description: 'Configure email notifications for bookings, reminders, and new booking alerts',
    icon: FiMail,
    path: '/settings/email',
    color: 'blue',
  },
  {
    id: 'policy',
    title: 'Booking Policy',
    description: 'Edit the booking policy template that users must accept before completing bookings',
    icon: FiFileText,
    path: '/settings/policy',
    color: 'purple',
  },
  {
    id: 'faqs',
    title: 'FAQs Management',
    description: 'Manage frequently asked questions for customers and the Nibi AI assistant',
    icon: FiHelpCircle,
    path: '/settings/faqs',
    color: 'indigo',
  },
  {
    id: 'voice',
    title: 'Voice Settings',
    description: 'Configure the voice for the Nibi AI assistant with sample playback',
    icon: FiVolume2,
    path: '/settings/voice',
    color: 'teal',
  },
  {
    id: 'seo',
    title: 'SEO Settings',
    description: 'Manage search engine optimization, metadata, structured data, and social sharing',
    icon: FiSearch,
    path: '/settings/seo',
    color: 'purple',
  },
];

const AdminSettingsHome: React.FC = () => {
  return (
    <Box>
      <Heading 
        size={{ base: 'md', md: 'lg' }}
        mb={2}
        pl={{ base: '60px', lg: 0 }}
        pr={{ base: 4, lg: 0 }}
      >
        Administrative Settings
      </Heading>
      <Text color="gray.600" mb={6}>
        Configure your app settings, business rules, and operational parameters
      </Text>

      <Alert status="info" mb={6}>
        <AlertIcon />
        <Box>
          <Text fontSize="sm" fontWeight="medium" mb={1}>
            Important
          </Text>
          <Text fontSize="sm">
            Changes to settings apply immediately and affect all new bookings and operations.
            Review settings carefully before saving.
          </Text>
        </Box>
      </Alert>

      <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} spacing={6}>
        {settingsOptions.map((option) => (
          <Card
            key={option.id}
            variant="outline"
            _hover={{
              boxShadow: 'md',
              borderColor: `${option.color}.400`,
            }}
            transition="all 0.2s"
          >
            <CardHeader>
              <HStack>
                <Box
                  p={3}
                  borderRadius="lg"
                  bg={`${option.color}.50`}
                  color={`${option.color}.600`}
                >
                  <Icon as={option.icon as React.ElementType} boxSize={6} />
                </Box>
                <Heading size="sm">{option.title}</Heading>
              </HStack>
            </CardHeader>
            <Divider />
            <CardBody>
              <VStack align="stretch" spacing={4}>
                <Text fontSize="sm" color="gray.600">
                  {option.description}
                </Text>
                <Link to={option.path} style={{ width: '100%' }}>
                  <Button
                    w="full"
                    colorScheme={option.color}
                    variant="outline"
                  >
                    Configure
                  </Button>
                </Link>
              </VStack>
            </CardBody>
          </Card>
        ))}
      </SimpleGrid>
    </Box>
  );
};

export default AdminSettingsHome;

